<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { computed, ref, useTemplateRef } from "vue";
import { vAutoFocus } from "#/directives/vAutoFocus";

interface Props {
	value?: string | null;
	hints?: string[];
	editable?: boolean;
	maxLength?: number;
}

interface Emits {
	submit: [comment: string];
	cancel: [];
	clear: [];
}

const MAX_LENGTH = 120;
// biome-ignore lint/suspicious/noControlCharactersInRegex: Это корректный набор для проверки чтобы исключить непечатаемые символы \0 \a \b \t \n \v \f \r \v
const COMMENT_CONTROL_CHARS = /[\x00\x07\x08\x09\x0A\x0B\x0C\x0D]/g;

const props = withDefaults(defineProps<Props>(), {
	value: "",
	editable: false,
	maxLength: MAX_LENGTH,
	hints: () => [],
});
const emit = defineEmits<Emits>();

const { t } = useTranslation();

const textareaRef = useTemplateRef<HTMLTextAreaElement>("textareaRef");
const rawComment = ref<string>("");
const isEditing = ref(false);

const comment = computed({
	get: () => rawComment.value,
	set: (value) => {
		rawComment.value = value.replace(COMMENT_CONTROL_CHARS, " ").trim().slice(0, props.maxLength);
	},
});
const state = computed(() => {
	if (props.editable && isEditing.value) return "edit";
	if (props.editable && !props.value?.length) return "create";

	return "preview";
});

const onEdit = () => {
	comment.value = props.value || "";
	isEditing.value = true;
};

const onSaveComment = () => {
	isEditing.value = false;
	emit("submit", comment.value);
};

const onCancel = () => {
	isEditing.value = false;
	emit("cancel");
};

const onClear = () => {
	emit("clear");
};

const onHint = (hint: string) => {
	comment.value = comment.value.length ? `${comment.value}, ${hint.charAt(0).toLowerCase() + hint.slice(1)}` : hint;
	textareaRef.value?.focus();
};
</script>

<template>
	<div v-if="editable || value?.length" class="cart_item_comment">
		<div class="cart_item_comment_create" v-if="state === 'create'">
			<button type="button" @click="onEdit">
				<svg width="16" height="16" viewBox="0 0 12 12" fill="none" aria-hidden="true">
					<path d="M6 1V11M1 6H11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
				</svg>
				<span>{{ t("cart-item-comment.create") }}</span>
			</button>
		</div>
		<div v-else-if="state === 'preview'" class="cart_item_comment_preview">
			<svg width="19" height="19" viewBox="0 0 14 14" fill="none" aria-hidden="true">
				<path
					d="M12.5 8.5C12.5 9.6 11.6 10.5 10.5 10.5H4.5L1.5 12.8V3.5C1.5 2.4 2.4 1.5 3.5 1.5H10.5C11.6 1.5 12.5 2.4 12.5 3.5V8.5Z"
					stroke="currentColor"
					stroke-width="1.3"
					stroke-linejoin="round"
				></path>
			</svg>
			<p>{{ value }}</p>
			<div v-if="editable" class="cart_item_comment_preview_actions">
				<button type="button" @click="onEdit">{{ t("cart-item-comment.edit") }}</button>
				<button type="button" @click="onClear">{{ t("cart-item-comment.clear") }}</button>
			</div>
		</div>
		<form v-else-if="state === 'edit'" class="cart_item_comment_filed" @submit.prevent="onSaveComment">
			<textarea
				ref="textareaRef"
				v-auto-focus
				v-model="comment"
				:maxlength="maxLength"
				:placeholder="t('cart-item-comment.placeholder')"
				@keydown.enter.prevent
			/>

			<div class="cart_item_comment_filed_hints" v-if="hints.length">
				<button type="button" @click="onHint(hint)" v-for="hint in hints" :key="hint">{{ hint }}</button>
			</div>

			<div class="cart_item_comment_filed_actions">
				<p class="cart_item_comment_length">{{ comment.length }} / {{ maxLength }}</p>

				<button type="button" @click="onCancel">
					{{ t("cart-item-comment.cancel") }}
				</button>
				<button type="submit" :disabled="!comment.length">
					{{ t("cart-item-comment.save") }}
				</button>
			</div>
		</form>
	</div>
</template>

<style lang="scss" scoped>
  @use './cartItemComment.scss';
</style>
